import React from 'react';
import { Box, Typography, Button, Paper, Chip, Stack, useTheme } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

/**
 * EmptyState - Displayed when search returns zero results
 * Provides helpful suggestions and example searches.
 *
 * @param {Object} props
 * @param {string} props.query - The search query that returned no results
 * @param {string} props.type - Type of search (e.g., 'hansard', 'parliamentarians', 'divisions')
 * @param {Function} props.onSuggestionClick - Callback when user clicks a suggestion
 * @param {Function} props.onReset - Callback to reset search
 * @param {string} props.themeColor - Theme color for styling (default: the theme's primary colour)
 */
export default function EmptyState({
  query,
  type = 'general',
  onSuggestionClick,
  onReset,
  themeColor = undefined
}) {
  const theme = useTheme();
  const color = themeColor || theme.palette.ledger.accentText;
  // Example searches based on content type
  const exampleSearches = {
    hansard: [
      { label: 'Climate policy', query: 'climate change' },
      { label: 'Budget debates', query: 'budget' },
      { label: 'Healthcare', query: 'medicare healthcare' },
      { label: 'Education', query: 'education schools' }
    ],
    parliamentarians: [
      { label: 'All members', query: '*' },
      { label: 'House of Reps', query: 'chamber:"House of Reps"' },
      { label: 'Senate', query: 'chamber:"Senate"' },
      { label: 'Labor Party', query: 'party:"Australian Labor Party"' }
    ],
    divisions: [
      { label: 'All divisions', query: '*' },
      { label: '2024 votes', query: 'date:[2024-01-01 TO 2024-12-31]' },
      { label: 'Close votes', query: '*' }
    ],
    calendar: [
      { label: 'Sitting days', query: 'event_type:"Sitting Day"' },
      { label: 'All events', query: '*' },
      { label: 'Committee meetings', query: 'committee' }
    ],
    tableddocuments: [
      { label: 'All documents', query: '*' },
      { label: 'Reports', query: 'document_type:"Report"' },
      { label: 'Responses', query: 'document_type:"Response"' }
    ],
    constitution: [
      { label: 'All sections', query: '*' },
      { label: 'Executive power', query: 'executive power' },
      { label: 'States', query: 'states' }
    ],
    general: [
      { label: 'Browse all', query: '*' }
    ]
  };

  const suggestions = exampleSearches[type] || exampleSearches.general;

  const searchTips = [
    'Try using fewer or different keywords',
    'Check your spelling',
    'Remove filters to broaden your search',
    'Use * for wildcard matches'
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        textAlign: 'center',
        borderRadius: 3,
        border: '1px dashed #ddd',
        bgcolor: '#fafafa'
      }}
    >
      <SearchOffIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />

      <Typography variant="h6" gutterBottom>
        No results found
      </Typography>

      {query && query !== '*' && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your search for "<strong>{query}</strong>" didn't match any records.
        </Typography>
      )}

      {/* Search tips */}
      <Box
        sx={{
          bgcolor: 'white',
          p: 2,
          borderRadius: 2,
          mb: 3,
          display: 'inline-block',
          textAlign: 'left'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LightbulbOutlinedIcon sx={{ color: color, fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Search tips:
          </Typography>
        </Box>
        <ul style={{ margin: 0, paddingLeft: 24 }}>
          {searchTips.map((tip, index) => (
            <li key={index}>
              <Typography variant="body2" color="text.secondary">
                {tip}
              </Typography>
            </li>
          ))}
        </ul>
      </Box>

      {/* Example searches */}
      {suggestions.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Try one of these searches:
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent="center"
            sx={{ gap: 1 }}
          >
            {suggestions.map((suggestion) => (
              <Chip
                key={suggestion.label}
                label={suggestion.label}
                onClick={() => onSuggestionClick?.(suggestion.query)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: `${color}15`,
                    borderColor: color
                  }
                }}
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Reset button */}
      {onReset && (
        <Box sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            onClick={onReset}
            sx={{
              borderRadius: 2,
              borderColor: color,
              color: color,
              '&:hover': {
                borderColor: color,
                bgcolor: `${color}10`
              }
            }}
          >
            Clear search & start over
          </Button>
        </Box>
      )}
    </Paper>
  );
}
